import type { ComponentType, SVGProps } from 'react';
import type { RouteGroup } from '@/auth/roles';
import {
  IconBell,
  IconBox,
  IconCash,
  IconCatalog,
  IconChart,
  IconClipboard,
  IconCredit,
  IconDoc,
  IconHome,
  IconLifebuoy,
  IconPlus,
  IconSettings,
  IconTag,
  IconTruck,
  IconUsers,
} from '@/components/icons';

export interface NavItem {
  /** Spanish label exactly as specified in docs/11. */
  label: string;
  /** Route path relative to the app root. */
  to: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
}

/**
 * Per-role Spanish sidebar navigation, taken verbatim from
 * docs/11_UI_UX_DESIGN_SYSTEM.md. Only items wired to real routes in this
 * scaffold link to a page; the rest are visible placeholders pointing at the
 * group's "próximamente" route so the IA is complete without dead links.
 */
export const NAV_BY_GROUP: Record<RouteGroup, NavItem[]> = {
  admin: [
    { label: 'Panel', to: '/app/admin', icon: IconHome },
    { label: 'Clientes', to: '/app/admin/clientes', icon: IconUsers },
    { label: 'Distribuidores', to: '/app/admin/distribuidores', icon: IconTruck },
    { label: 'Vendedores', to: '/app/admin/vendedores', icon: IconUsers },
    { label: 'Productos', to: '/app/admin/productos', icon: IconBox },
    { label: 'Marcas y categorías', to: '/app/admin/marcas-categorias', icon: IconCatalog },
    { label: 'Inventario', to: '/app/admin/inventario', icon: IconClipboard },
    { label: 'Pedidos', to: '/app/admin/pedidos', icon: IconClipboard },
    { label: 'Cotizaciones', to: '/app/admin/cotizaciones', icon: IconDoc },
    { label: 'Facturas', to: '/app/admin/facturas', icon: IconDoc },
    { label: 'Pagos', to: '/app/admin/pagos', icon: IconCash },
    { label: 'Documentos', to: '/app/admin/documentos', icon: IconDoc },
    { label: 'Crédito', to: '/app/admin/credito', icon: IconCredit },
    { label: 'Promociones', to: '/app/admin/promociones', icon: IconTag },
    { label: 'Reportes', to: '/app/admin/reportes', icon: IconChart },
    { label: 'Notificaciones', to: '/app/admin/notificaciones', icon: IconBell },
    { label: 'Configuración', to: '/app/admin/configuracion', icon: IconSettings },
  ],
  sales: [
    { label: 'Inicio', to: '/app/sales', icon: IconHome },
    { label: 'Clientes', to: '/app/sales/clientes', icon: IconUsers },
    { label: 'Crear pedido', to: '/app/sales/crear-pedido', icon: IconPlus },
    { label: 'Pedidos', to: '/app/sales/pedidos', icon: IconClipboard },
    { label: 'Cotizaciones', to: '/app/sales/cotizaciones', icon: IconDoc },
    { label: 'Facturas', to: '/app/sales/facturas', icon: IconDoc },
    { label: 'Pagos', to: '/app/sales/pagos', icon: IconCash },
    { label: 'Notificaciones', to: '/app/sales/notificaciones', icon: IconBell },
  ],
  customer: [
    { label: 'Inicio', to: '/app/customer', icon: IconHome },
    { label: 'Catálogo', to: '/app/customer/catalogo', icon: IconCatalog },
    { label: 'Crear pedido', to: '/app/customer/crear-pedido', icon: IconPlus },
    { label: 'Mis pedidos', to: '/app/customer/pedidos', icon: IconClipboard },
    { label: 'Facturas', to: '/app/customer/facturas', icon: IconDoc },
    { label: 'Pagos', to: '/app/customer/pagos', icon: IconCash },
    { label: 'Promociones', to: '/app/customer/promociones', icon: IconTag },
    { label: 'Documentos', to: '/app/customer/documentos', icon: IconDoc },
    { label: 'Notificaciones', to: '/app/customer/notificaciones', icon: IconBell },
    { label: 'Soporte', to: '/app/customer/soporte', icon: IconLifebuoy },
  ],
  distributor: [
    { label: 'Panel', to: '/app/distributor', icon: IconHome },
    { label: 'Catálogo mayorista', to: '/app/distributor/catalogo', icon: IconCatalog },
    { label: 'Crear pedido', to: '/app/distributor/crear-pedido', icon: IconPlus },
    { label: 'Pedidos', to: '/app/distributor/pedidos', icon: IconClipboard },
    { label: 'Subclientes', to: '/app/distributor/subclientes', icon: IconUsers },
    { label: 'Facturas', to: '/app/distributor/facturas', icon: IconDoc },
    { label: 'Pagos', to: '/app/distributor/pagos', icon: IconCash },
    { label: 'Mi cuenta', to: '/app/distributor/cuenta', icon: IconSettings },
    { label: 'Notificaciones', to: '/app/distributor/notificaciones', icon: IconBell },
  ],
  // Warehouse nav is not enumerated in docs/11; a practical fulfillment set
  // derived from docs/05 (warehouse can view orders, pick/pack/ship, inventory).
  warehouse: [
    { label: 'Inicio', to: '/app/warehouse', icon: IconHome },
    { label: 'Pedidos', to: '/app/warehouse/pedidos', icon: IconClipboard },
    { label: 'Inventario', to: '/app/warehouse/inventario', icon: IconBox },
    { label: 'Despachos', to: '/app/warehouse/despachos', icon: IconTruck },
    { label: 'Notificaciones', to: '/app/warehouse/notificaciones', icon: IconBell },
  ],
};

export const GROUP_TITLES_ES: Record<RouteGroup, string> = {
  admin: 'Administración',
  sales: 'Ventas',
  customer: 'Portal de cliente',
  distributor: 'Portal de distribuidor',
  warehouse: 'Almacén',
};
